import {
  ActionIcon,
  Group,
  NumberFormatter,
  Paper,
  RingProgress,
  SegmentedControl,
  Stack,
  Text,
  ThemeIcon,
  Tooltip,
} from "@mantine/core";
import { IconExternalLink, IconRocket } from "@tabler/icons-react";
import { useMemo, useState } from "react";
import { useReportingStore } from "../../../stores/ReportingStore";
import { Link } from "react-router-dom";
import ReportingInfo from "../reporting/ReportingInfo";
import {
  ArchiveIcon,
  PauseIcon,
  PeopleCommunityIcon,
} from "../../../components/Icons/SvgIcons";
import { useLanguage } from "../../../stores/LanguageStore";

const HomeReporting = () => {
  const lang = useLanguage((s) => s.language);
  const [active, setActive] = useState("campaigns");

  const data = [
    {
      label: lang.home.reports.campaigns.label,
      value: "campaigns",
      component: <Campaigns />,
    },
    {
      label: lang.home.reports.audiences.label,
      value: "audience",
      component: <Audience />,
    },
    {
      label: lang.home.reports.perf.label,
      value: "perf",
      component: <Performance />,
    },
  ];

  return (
    <Stack w={820} mx={"auto"} id="home-reporting">
      <SegmentedControl
        size="lg"
        value={active}
        onChange={setActive}
        data={data}
        p={8}
      />

      <Paper
        p={"md"}
        component={Stack}
        justify="center"
        flex={1}
        pos={"relative"}
      >
        {data.find((x) => x.value === active).component}

        <Tooltip label={lang.home.reports.to_reports}>
          <ActionIcon
            component={Link}
            to={"/reporting"}
            pos={"absolute"}
            right={-48}
            size={"lg"}
            variant="light"
          >
            <IconExternalLink size={22} />
          </ActionIcon>
        </Tooltip>
      </Paper>
    </Stack>
  );
};

const Campaigns = () => {
  const lang = useLanguage((s) => s.language);
  const global_stats = useReportingStore((s) => s.globalStats);
  return (
    <Group fz={24} gap={"xs"}>
      <ThemeIcon variant="subtle" size={48} mr={-8}>
        <IconRocket size={32} />
      </ThemeIcon>

      <NumberFormatter
        style={{ fontWeight: 600 }}
        value={global_stats.programmation_running}
      />
      <Text fz={20} fw={400}>
        {lang.home.reports.campaigns.active}
      </Text>

      <PauseIcon size={32} ml={"auto"} />

      <NumberFormatter
        style={{ fontWeight: 600 }}
        value={global_stats.programmation_stopped}
      />
      <Text fz={20} fw={400}>
        {lang.home.reports.campaigns.paused}
      </Text>

      <ArchiveIcon size={32} ml={"auto"} />

      <NumberFormatter
        style={{ fontWeight: 600 }}
        value={global_stats.programmation_archived}
      />
      <Text fz={20} fw={400}>
        {lang.home.reports.campaigns.archived}
      </Text>
    </Group>
  );
};

const Audience = () => {
  const lang = useLanguage((s) => s.language);
  const statistics = useReportingStore((s) => s.actualStats);
  return (
    <Group>
      <ThemeIcon variant="subtle" size={48}>
        <PeopleCommunityIcon size={32} />
      </ThemeIcon>

      <Text fz={20} fw={600}>
        <NumberFormatter value={statistics?.contacts_unique || 0} />{" "}
        {lang.home.reports.audiences.contacted}
      </Text>
      <Text
        size="lg"
        ml={"auto"}
        bg={"primary.0"}
        p={"xs"}
        py={4}
        style={{ borderRadius: 8 }}
      >
        <NumberFormatter
          value={statistics.email_sent}
          style={{ fontWeight: 700 }}
        />{" "}
        {lang.home.reports.audiences.email}{" "}
        <NumberFormatter
          value={statistics.linkedin_sent}
          style={{ fontWeight: 700 }}
        />{" "}
        {lang.home.reports.audiences.linkedin}
      </Text>
    </Group>
  );
};

function handleConclusion(rate) {
  if (rate >= 5) {
    return "tertiary.8";
  } else if (rate >= 2) {
    return "orange";
  }
  return "red";
}
const Performance = () => {
  const lang = useLanguage((s) => s.language);
  const statistics = useReportingStore((s) => s.actualStats);
  const emailOpenerRate = useMemo(() => {
    if (statistics.email_sent) {
      return (statistics.opener_total / statistics.email_sent) * 100;
    }
    return 0;
  }, [statistics.opener_total, statistics.email_sent]);

  const emailClickerRate = useMemo(() => {
    if (statistics.opener_total) {
      return (statistics.clicker_total / statistics.opener_total) * 100;
    }
    return 0;
  }, [statistics.clicker_total, statistics.opener_total]);

  const emailAnswerRate = useMemo(() => {
    if (statistics.email_sent) {
      return (statistics.email_answer / statistics.email_sent) * 100;
    }
    return 0;
  }, [statistics.email_answer, statistics.email_sent]);

  const linkedinAnswerRate = useMemo(() => {
    if (
      statistics.linkedin_invitation_sent ||
      statistics.linkedin_message_sent
    ) {
      return (
        ((statistics.linkedin_invitation_accepted +
          statistics.linkedin_message_answered) /
          (statistics.linkedin_invitation_sent +
            statistics.linkedin_message_sent)) *
        100
      );
    }
    return 0;
  }, [
    statistics.linkedin_invitation_accepted,
    statistics.linkedin_invitation_sent,
    statistics.linkedin_message_answered,
    statistics.linkedin_message_sent,
  ]);

  const emails = [
    { value: emailOpenerRate, label: lang.home.reports.perf.opened },
    { value: emailClickerRate, label: lang.home.reports.perf.clicks },
    {
      value: emailAnswerRate + linkedinAnswerRate,
      label: lang.home.reports.perf.answers,
      info: (
        <ReportingInfo>
          <Text>{lang.home.reports.perf.answers_tooltip}</Text>
        </ReportingInfo>
      ),
    },
  ];

  return (
    <Stack>
      <Group justify="space-between" flex={1}>
        {/* <Stack gap={0} mr={32}>
          <Text ta={"center"} c={"#2C3642"}>Emails</Text>
        <ThemeIcon variant="subtle" size={48} color="orange">
          <svg
            width="28"
            viewBox="0 0 20 17"
            fill="#2C3642"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M16 0H4C1.79086 0 0 1.79086 0 4V13C0 15.2091 1.79086 17 4 17H16C18.2091 17 20 15.2091 20 13V4C20 1.79086 18.2091 0 16 0ZM4 1.59H16C17.0657 1.59204 18.0025 2.29663 18.3 3.32L10.76 8.91C10.5534 9.11204 10.2732 9.22095 9.98433 9.21151C9.69548 9.20207 9.42301 9.0751 9.23 8.86L1.72 3.33C2.01175 2.30973 2.9389 1.60216 4 1.59ZM1.59 13C1.59 14.331 2.66899 15.41 4 15.41H16C17.3271 15.4045 18.4 14.3271 18.4 13V4.97L11.6 9.97C11.1654 10.3746 10.5938 10.5997 10 10.6C9.38274 10.5902 8.79115 10.3514 8.34 9.93L1.59 4.93V13Z"
            />
          </svg>
        </ThemeIcon>

        </Stack> */}

        {emails.map((item, i) => (
          <Group key={i}>
            <RingProgress
              label={
                <Group justify="center" ml={12} mt={-8}>
                  <Text size={11} fw={600} ta="center">
                    {item.value.toFixed(1)}%
                  </Text>
                </Group>
              }
              roundCaps={item.value < 92}
              sections={[
                { value: item.value, color: handleConclusion(item.value) },
              ]}
              size={64}
              thickness={6}
            />
            <Text size="md" w={96} mr={-32}>
              {item.label}
            </Text>

            {item.info}
          </Group>
        ))}
      </Group>

      {/* <Divider mx={64} />

      <Group justify="space-between" flex={1}>
        <ThemeIcon variant="subtle" size={48} color="blue" mr={32}>
          <IconBrandLinkedin size={32} />
        </ThemeIcon>

        {linkedin.map((item, i) =>
          item.label === "-" ? (
            <Group key={i} w={177} justify="center">
              -
            </Group>
          ) : (
            <Group key={i}>
              <RingProgress
                label={
                  <Group justify="center" ml={12} mt={-8}>
                    <Text size="sm" fw={600} ta="center">
                      {item.value.toFixed(1)}%
                    </Text>
                  </Group>
                }
                roundCaps={item.value < 92}
                sections={[
                  { value: item.value, color: handleConclusion(item.value) },
                ]}
                size={64}
                thickness={6}
              />
              <Text size="md" w={96}>
                {item.label}
              </Text>
            </Group>
          )
        )}
      </Group> */}
    </Stack>
  );
};

export default HomeReporting;
